import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { PageEvent } from "@angular/material/paginator";
import {
  faPen, faTrash, faUser, faEye,
  faCalendar, faPlus, faBan, faCheck,
  faDownload, faUpload, faLink,
} from "@fortawesome/free-solid-svg-icons";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Observable, of, Subject } from "rxjs";
import { catchError } from "rxjs/operators";

import { empty } from "../../functions";
import { ApiService } from "../../shared/services/api.service";
import {
  alertMessages,
  AlertService,
} from "../../shared/services/alert.service";

@Component({
  selector: "app-table",
  templateUrl: "./table.component.html",
  standalone: false,
  host: { style: "display: block; min-width: 0;" },
})
export class TableComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  readonly api = inject(ApiService);
  private readonly alertService = inject(AlertService);

  empty = empty;

  isLoading = false;

  @Output() buttonEvent = new EventEmitter();

  @Input() config: any = {
    hasPagination: true,
    endpoint: "",
    queryParams: {},
    columns: <any>[],
    class: "",
    data: <any | null>[],
    buttons: <any>[],
    reloadListener: <any | null>null,
  };

  // Base table structure
  tableStructure = {
    tableColumns: <any>[],
    data: <any>[],
    page: 1,
    limit: 100,
    total_results: 0,
    total_pages: 1,
    order: "",
    orderby: "",
  };

  ngOnInit(): void {
    if (!empty(this.config.columns)) {
      this.config.columns.forEach((col: any, index: number) => {
        const columnData = {
          colId: `col_${index}`,        // unique key for matColumnDef (name may repeat)
          header_name: col["header_name"],
          name: col["name"],
          subName: !empty(col["subName"]) ? col["subName"] : "",
          type: !empty(col["type"]) ? col["type"] : "",
          field: !empty(col["field"]) ? col["field"] : null,
          class: !empty(col["class"]) ? col["class"] : null,
        };

        this.tableStructure.tableColumns.push(columnData);
      });
    }

    this.loadData();

    if (this.config.reloadListener instanceof Subject) {
      this.config.reloadListener
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.loadData(true);
        });
    }
  }

  /**
   * Load data
   *
   * @param $reload
   */
  loadData($reload = false) {
    if (!$reload) {
      this.isLoading = true;
    }

    if (!empty(this.config.endpoint)) {
      let queryParams: any = {};
      if (this.config.hasPagination) {
        queryParams.pageNumber = this.tableStructure.page;
        queryParams.pageSize = this.tableStructure.limit;
      }

      // Merge query params of config if given
      if (this.config.hasOwnProperty("queryParams")) {
        queryParams = Object.assign(queryParams, this.config.queryParams);
        for (const [key, value] of Object.entries(queryParams)) {
          if (value === null) delete queryParams[key];
        }
      }

      this.api.setEndpoint(this.config.endpoint);
      this.api
        .GET(queryParams)
        .pipe(catchError(this.handleError("", false)))
        .subscribe((result: any) => {
          this.isLoading = false;
          if (!empty(result)) {
            if (this.config.hasPagination) {
              this.tableStructure.data = Object.assign([], result.data);
              this.tableStructure.total_results = result.totalItems;
            } else {
              this.tableStructure.data = Object.assign([], result);
              this.tableStructure.total_results = result.length;
            }
          } else {
            this.tableStructure.data = [];
            this.tableStructure.total_results = 0;
          }
        });
    }
  }

  /** Map from button event name → FontAwesome icon. */
  readonly buttonIcons: Record<string, any> = {
    edit: faPen,
    delete: faTrash,
    remove: faTrash,
    profile: faUser,
    view: faEye,
    "election-signup": faCalendar,
    signup: faCalendar,
    add: faPlus,
    approve: faCheck,
    reject: faBan,
    ban: faBan,
    download: faDownload,
    upload: faUpload,
    link: faLink,
  };

  getButtonIcon(event: string): any {
    return this.buttonIcons[event] ?? null;
  }

  getButtonClass(bootstrapClass: string): string {
    const base = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border transition-colors focus:outline-none cursor-pointer whitespace-nowrap';
    if (bootstrapClass?.includes('btn-info'))    return `${base} bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100`;
    if (bootstrapClass?.includes('btn-danger'))  return `${base} bg-red-50 text-red-700 border-red-200 hover:bg-red-100`;
    if (bootstrapClass?.includes('btn-warning')) return `${base} bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100`;
    if (bootstrapClass?.includes('btn-success')) return `${base} bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100`;
    if (bootstrapClass?.includes('btn-primary')) return `${base} bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100`;
    return `${base} bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100`;
  }

  /** Column IDs passed to mat-table's *matHeaderRowDef / *matRowDef. */
  get displayedColumns(): string[] {
    const cols = this.tableStructure.tableColumns.map((c: any) => c.colId);
    if (this.config.buttons?.length) cols.push("__actions");
    return cols;
  }

  /** Called by mat-paginator (0-based pageIndex → 1-based API page). */
  onPageChange(event: PageEvent): void {
    this.tableStructure.page = event.pageIndex + 1;
    this.loadData();
  }

  /**
   * Set total pages
   */
  setTotalPages() {
    this.tableStructure.total_pages =
      this.tableStructure.total_results > 0
        ? Math.ceil(
            this.tableStructure.total_results / this.tableStructure.limit
          )
        : 1;
  }

  /**
   * Set (current) page
   *
   * To use with pagination
   *
   * @param $value
   */
  setPage($value: any) {
    this.tableStructure.page = $value;
    this.loadData();
  }

  /**
   * Error handler
   *
   * @param operation
   * @param result
   */
  handleError<T>(operation: string = "", result?: T) {
    return (error: unknown): Observable<T> => {
      // Handle given operation
      switch (operation) {
        default:
          this.alertService.errorWithReport(error, `table:${operation}`);
          break;
      }

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
